import {
  Button,
  LabeledDatePickerInput,
  LabeledSelect,
} from "@apex/component-library";
import { Box, Stack, Text, Wrap, WrapItem } from "@chakra-ui/react";
import { useFormikContext } from "formik";
import { DateTime } from "luxon";
import React, { useEffect } from "react";

import { DISPLAY_DATE_FORMAT } from "../../../utils/dateFormatter";
import { RequirementsSearchBarValues } from "./utils/requirementsSchema";

const HEIGHT_FOR_ERROR = "5.3rem";

type Props = { isFetching: boolean };

export const getDatePickerFormattedDate = (date: string | Date): string => {
  if (date instanceof Date) {
    return DateTime.fromJSDate(date).toFormat(DISPLAY_DATE_FORMAT);
  }
  return date || "";
};

const RequirementsSearchForm = ({ isFetching }: Props) => {
  const formik = useFormikContext<RequirementsSearchBarValues>();

  const {
    values,
    isValid,
    setFieldValue,
    handleBlur,
    handleChange,
    touched,
    errors,
    submitForm,
  } = formik;

  const handleSetDate = (fieldName: string, date: string | Date) => {
    setFieldValue(fieldName, getDatePickerFormattedDate(date));
  };

  useEffect(() => {
    setTimeout(() => {
      if (isValid) {
        submitForm();
      }
    }, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSearch = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    submitForm();
  };
  return (
    <form onSubmit={onSearch}>
      <Wrap
        bg="gray.100"
        border="1px solid"
        borderColor="gray.300"
        w="100%"
        padding={3}
        align="flex-end"
        spacing="1rem"
        width="100%"
        data-testid="requirements-search-form-wrap"
      >
        <WrapItem h={HEIGHT_FOR_ERROR}>
          <Stack direction="row" spacing="0.5rem" alignItems="center">
            <Box
              width="input.md"
              h={HEIGHT_FOR_ERROR}
              data-testid="from-process-date-wrap"
            >
              <LabeledDatePickerInput
                label="From (Process Date)"
                name="fromProcessDate"
                onChange={(date: string | Date) => {
                  handleSetDate("fromProcessDate", date);
                }}
                onBlur={handleBlur}
                date={values.fromProcessDate || ""}
                placement="bottom"
                isInvalid={
                  (touched.fromProcessDate && !!errors.fromProcessDate) ||
                  (!!errors.fromProcessDate && !!touched.toProcessDate)
                }
                errorMessage={errors.fromProcessDate}
                disableFutureDates
                data-testid="from-process-date-input"
                isRequired
                isDisabled={isFetching}
              />
            </Box>
            <Box display="flex">
              <Text
                as="p"
                data-testid="process-date-spacer"
                paddingTop="0.8rem"
              >
                &mdash;
              </Text>
            </Box>
            <Box
              width="input.md"
              h={HEIGHT_FOR_ERROR}
              data-testid="to-process-date-wrap"
            >
              <LabeledDatePickerInput
                label="To (Process Date)"
                name="toProcessDate"
                onChange={(date: string | Date) => {
                  handleSetDate("toProcessDate", date);
                }}
                onBlur={handleBlur}
                date={values.toProcessDate}
                placement="bottom"
                isInvalid={touched.toProcessDate && !!errors.toProcessDate}
                errorMessage={errors.toProcessDate}
                disableFutureDates
                data-testid="to-process-date-input"
                isRequired
                isDisabled={isFetching}
              />
            </Box>
          </Stack>
        </WrapItem>
        <WrapItem h={HEIGHT_FOR_ERROR} width="input.md">
          <LabeledSelect
            label="View"
            name="view"
            isRequired
            isDisabled={isFetching}
            onChange={handleChange}
            value={values.view}
            data-testid="view-select"
          >
            <option value="details">Details</option>
            <option value="summary">Summary</option>
          </LabeledSelect>
        </WrapItem>
        <WrapItem
          h={HEIGHT_FOR_ERROR}
          width="120px"
          alignItems="center"
          paddingTop="0.75rem"
        >
          <Stack direction="row" spacing="1rem" alignItems="center">
            <Button
              variant="primary"
              type="submit"
              isDisabled={!isValid}
              isLoading={!!isFetching}
              data-testid="search-btn"
            >
              Search
            </Button>
          </Stack>
        </WrapItem>
      </Wrap>
    </form>
  );
};

export default RequirementsSearchForm;
